import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { parseHashQueryParams } from '../../utils/hashQueryParams';
import './LoginModal.css';

interface ResetPasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ResetPasswordModal: React.FC<ResetPasswordModalProps> = ({ isOpen, onClose }) => {
  const API_BASE = process.env.REACT_APP_AUTH_API_URL || 'https://api.datacircle.ru';
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const validatePassword = (pwd: string) => {
    return (
      pwd.length >= 8 &&
      /[A-Z]/.test(pwd) &&
      /[a-z]/.test(pwd) &&
      /\d/.test(pwd)
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!validatePassword(password)) {
      setError('Пароль должен содержать минимум 8 символов, заглавную букву, строчную букву и цифру');
      return;
    }
    if (password !== confirmPassword) {
      setError('Пароли не совпадают');
      return;
    }

    setLoading(true);
    try {
      const token = parseHashQueryParams(window.location.hash)['reset_token'] || '';
      if (!token) {
        throw new Error('Токен сброса не найден');
      }
      const response = await fetch(`${API_BASE}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });
      if (!response.ok) throw new Error('Не удалось изменить пароль');

      setSuccess('Пароль успешно изменён!');
      setTimeout(() => {
        onClose();
        navigate('/workspace');
      }, 2000);
    } catch (err: any) {
      setError(err.message || 'Не удалось изменить пароль. Попробуйте снова.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="auth-modal__overlay">
      <div className="auth-modal__dialog" onClick={(e) => e.stopPropagation()}>
        <div className="auth-modal__header">
          <h2 className="auth-modal__title">Новый пароль</h2>
        </div>

        <div className="auth-modal__body">
          <p className="auth-modal__subtitle">
            Придумайте новый пароль для вашего аккаунта
          </p>

          {error && <div className="auth-modal__error">{error}</div>}
          {success && <div className="auth-modal__success">{success}</div>}

          {!success && (
            <form onSubmit={handleSubmit} className="auth-modal__form">
              <div className="auth-modal__field">
                <label htmlFor="new-password" className="auth-modal__label">Новый пароль</label>
                <input
                  type="password"
                  id="new-password"
                  className="auth-modal__input"
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setError(''); }}
                  disabled={loading}
                  autoComplete="new-password"
                />
                <div className="auth-modal__pwd-req">
                  <div className={`auth-modal__req ${password.length >= 8 ? 'auth-modal__req--valid' : 'auth-modal__req--invalid'}`}>
                    <i className={`fas fa-${password.length >= 8 ? 'check' : 'circle'}`}></i>
                    Минимум 8 символов
                  </div>
                  <div className={`auth-modal__req ${/[A-Z]/.test(password) ? 'auth-modal__req--valid' : 'auth-modal__req--invalid'}`}>
                    <i className={`fas fa-${/[A-Z]/.test(password) ? 'check' : 'circle'}`}></i>
                    Заглавная буква
                  </div>
                  <div className={`auth-modal__req ${/[a-z]/.test(password) ? 'auth-modal__req--valid' : 'auth-modal__req--invalid'}`}>
                    <i className={`fas fa-${/[a-z]/.test(password) ? 'check' : 'circle'}`}></i>
                    Строчная буква
                  </div>
                  <div className={`auth-modal__req ${/\d/.test(password) ? 'auth-modal__req--valid' : 'auth-modal__req--invalid'}`}>
                    <i className={`fas fa-${/\d/.test(password) ? 'check' : 'circle'}`}></i>
                    Цифра
                  </div>
                </div>
              </div>

              <div className="auth-modal__field">
                <label htmlFor="confirm-new-password" className="auth-modal__label">Подтвердите пароль</label>
                <input
                  type="password"
                  id="confirm-new-password"
                  className="auth-modal__input"
                  value={confirmPassword}
                  onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
                  disabled={loading}
                  autoComplete="new-password"
                />
              </div>

              <button type="submit" className="auth-modal__submit" disabled={loading}>
                {loading ? 'Сохраняем…' : 'Сохранить новый пароль'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordModal;
